import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import {
  CircularProgress,
  TextField,
  MenuItem,
  Box,
  Typography,
  FormControlLabel,
  Tooltip,
  Button,
  Radio,
  RadioGroup,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Info } from "@mui/icons-material";
import { apiClient } from "../../../../api/httpClient";
import { scrollToTop } from "../formHelpers";
import { buildBillingPayload, buildRetrievalBillingPayload, getInitialBillingEntity } from "../billingSurfaceConfig";

const BondBillingInformationBase = ({
  cfg,
  GoogleAutoComplete,
  LabeledCreatableSelect,
  data,
  updateData,
  globalData,
  isGuest = false,
  isDisabled = false,
}) => {
  const guardians = globalData.guardianDetails?.guardians;
  const hasForeignCitizenship = cfg.hasForeignCitizenship?.(guardians) ?? false;
  const hasPGPreferredBilling =
    cfg.supportsPgPreferredBilling &&
    guardians?.some((guardian) => guardian.preferred_billing === true);

  const emitBillingUpdate = useCallback(
    (info) => {
      if (cfg.includeCustNoInPayload) {
        updateData(buildRetrievalBillingPayload(info));
        return;
      }
      updateData(
        buildBillingPayload(info, {
          includeBillingOverride: cfg.includeBillingOverrideInPayload,
        })
      );
    },
    [cfg.includeBillingOverrideInPayload, cfg.includeCustNoInPayload, updateData]
  );

  const [billingInfo, setBillingInfo] = useState({
    billing_entity: cfg.useEmptyInitialBillingEntity
      ? ""
      : getInitialBillingEntity({ cfg, globalData, isGuest, guardians }),
    billing_lawfirmaddress_name: "",
    billing_principal_name: "",
    billing_other_name: "",
    billing_other_address: "",
    billing_other_relationship: "",
    billing_other_city: "",
    billing_other_state: "",
    billing_other_zip: "",
    ...(cfg.includeCustNoInPayload ? { custNo: "" } : {}),
    is_billing_override: false,
  });

  const [billingOverrideModalOpen, setBillingOverrideModalOpen] = useState(false);
  const [pendingBillingEntity, setPendingBillingEntity] = useState(null);

  const [principalOptions, setPrincipalOptions] = useState([]);
  const [lawFirmLocations, setLawFirmLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [loadingCitiesStates, setLoadingCitiesStates] = useState(true);

  useEffect(() => {
    const fetchPrincipalOptions = () => {
      setLoading(true);
      try {
        const principals = guardians?.map(cfg.mapPrincipal);
        setPrincipalOptions(principals || []);

        if (cfg.historicFetchEntitySideEffects) {
          setBillingInfo((prevState) => ({
            ...prevState,
            billing_entity: "Principal",
          }));
        }
      } catch (error) {
        console.error("Error fetching principals:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchLawFirmLocations = async () => {
      setLoading(true);
      try {
        if (cfg.supportsGuestFlow && isGuest) {
          const guestName =
            globalData?.attorneyInformation?.guest_lawfirm_name || "Guest Law Firm";
          const guestAddress =
            globalData?.attorneyInformation?.guest_lawfirmaddress_name || "";
          setLawFirmLocations([
            {
              label: guestName,
              value: guestAddress || guestName,
              address: guestAddress,
            },
          ]);
          return;
        }

        const lawFirmId = globalData?.attorneyInformation?.lawFirm;
        if (!lawFirmId) {
          setLawFirmLocations([]);
          return;
        }

        const response = await apiClient(`/get-locations-by-lawfirm/${lawFirmId}`, "GET");
        const formattedLocations = response.map(cfg.formatLawFirmLocation);
        setLawFirmLocations(formattedLocations);

        if (cfg.historicFetchEntitySideEffects) {
          setBillingInfo((prevState) => ({
            ...prevState,
            billing_entity: "Law Firm",
          }));
        }
      } catch (error) {
        console.error("Error fetching law firm locations:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchCitiesAndStates = async () => {
      try {
        const cities = await apiClient("/fields/city/", "GET");
        const states = await apiClient("/fields/states/", "GET");
        setAllCities(cities);
        setAllStates(states);
      } catch (error) {
        console.error("Error loading cities or states:", error);
      } finally {
        setLoadingCitiesStates(false);
      }
    };

    fetchPrincipalOptions();
    fetchLawFirmLocations();
    fetchCitiesAndStates();
  }, [
    cfg,
    guardians,
    globalData?.attorneyInformation,
    isGuest,
  ]);

  useEffect(() => {
    if (!cfg.supportsGuestAutoSelectLawFirm) return;
    const isGuestBond = !!globalData?.attorneyInformation?.is_guest;
    if (!isGuestBond) return;
    if (billingInfo.billing_entity !== "Law Firm") return;
    if (!Array.isArray(lawFirmLocations) || lawFirmLocations.length === 0) return;

    const current = billingInfo.billing_lawfirmaddress_name;
    const stillValid = current && lawFirmLocations.some((l) => l.value === current);
    if (stillValid) return;

    const selectedLocation = lawFirmLocations[0]?.value || "";
    if (!selectedLocation) return;

    const nextInfo = {
      ...billingInfo,
      billing_entity: "Law Firm",
      billing_lawfirmaddress_name: selectedLocation,
    };
    setBillingInfo(nextInfo);
    emitBillingUpdate(nextInfo);
  }, [
    cfg.supportsGuestAutoSelectLawFirm,
    globalData?.attorneyInformation?.is_guest,
    globalData?.attorneyInformation?.lawFirm,
    billingInfo,
    lawFirmLocations,
    emitBillingUpdate,
  ]);

  useEffect(() => {
    if (!hasForeignCitizenship) return;
    if (billingInfo.billing_entity === "Law Firm") return;

    const preferredLocation =
      globalData?.attorneyInformation?.lawFirmAddress ||
      billingInfo.billing_lawfirmaddress_name ||
      "";
    const next = {
      ...billingInfo,
      billing_entity: "Law Firm",
      billing_lawfirmaddress_name: preferredLocation,
    };
    setBillingInfo(next);
    emitBillingUpdate(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasForeignCitizenship]);

  useEffect(() => {
    if (billingInfo.billing_entity === "Law Firm") {
      if (lawFirmLocations.length === 1) {
        const selectedLocation = lawFirmLocations[0].value;
        setBillingInfo((prevState) => ({
          ...prevState,
          billing_lawfirmaddress_name: selectedLocation,
        }));

        emitBillingUpdate({
          ...billingInfo,
          billing_entity: "Law Firm",
          billing_lawfirmaddress_name: selectedLocation,
        });
      }
    } else if (billingInfo.billing_entity === "Principal") {
      if (principalOptions.length === 1) {
        const selectedPrincipal = principalOptions[0].value;
        setBillingInfo((prevState) => ({
          ...prevState,
          billing_principal_name: selectedPrincipal,
        }));

        emitBillingUpdate({
          ...billingInfo,
          billing_entity: "Principal",
          billing_principal_name: selectedPrincipal,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingInfo.billing_entity, principalOptions, lawFirmLocations]);

  useEffect(() => {
    if (!data) return;

    setBillingInfo((prevState) => ({
      ...prevState,
      billing_entity: data.billing_entity || prevState.billing_entity,
      billing_lawfirmaddress_name:
        data.billing_lawfirmaddress_name || prevState.billing_lawfirmaddress_name,
      billing_principal_name:
        data.billing_principal_name || prevState.billing_principal_name,
      billing_other_name: data.billing_other_name || prevState.billing_other_name,
      billing_other_address:
        data.billing_other_address || prevState.billing_other_address,
      billing_other_relationship:
        data.billing_other_relationship || prevState.billing_other_relationship,
      billing_other_city: data.billing_other_city || prevState.billing_other_city,
      billing_other_state: data.billing_other_state || prevState.billing_other_state,
      billing_other_zip: data.billing_other_zip || prevState.billing_other_zip,
      ...(cfg.includeCustNoInPayload
        ? { custNo: data.custNo || prevState.custNo }
        : {}),
    }));
  }, [cfg.includeCustNoInPayload, data]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (
      cfg.supportsBillingOverride &&
      name === "billing_entity" &&
      value === "Law Firm" &&
      hasPGPreferredBilling &&
      !hasForeignCitizenship
    ) {
      setPendingBillingEntity("Law Firm");
      setBillingOverrideModalOpen(true);
      return;
    }

    const updatedBillingInfo = {
      ...billingInfo,
      [name]: value,
      ...(name === "billing_entity" && cfg.includeBillingOverrideInPayload
        ? { is_billing_override: false }
        : {}),
    };
    setBillingInfo(updatedBillingInfo);
    emitBillingUpdate(updatedBillingInfo);
  };

  const handleOverrideConfirm = () => {
    const updatedBillingInfo = {
      ...billingInfo,
      billing_entity: pendingBillingEntity,
      is_billing_override: true,
    };
    setBillingInfo(updatedBillingInfo);
    emitBillingUpdate(updatedBillingInfo);
    setBillingOverrideModalOpen(false);
    setPendingBillingEntity(null);
  };

  const handleOverrideCancel = () => {
    setBillingOverrideModalOpen(false);
    setPendingBillingEntity(null);
  };

  const handleOtherAddressAutocomplete = (address, city, state, zipCode) => {
    setBillingInfo((prev) => ({
      ...prev,
      billing_other_address: address,
      billing_other_city: city,
      billing_other_state: state,
      billing_other_zip: zipCode,
    }));
    updateData({
      ...data,
      billing_other_address: address,
      billing_other_city: city,
      billing_other_state: state,
      billing_other_zip: zipCode,
    });
  };

  const handleOtherCityChange = (opt) => {
    setBillingInfo((prev) => ({
      ...prev,
      billing_other_city: opt ? opt.value : "",
    }));
    updateData({
      ...data,
      billing_other_city: opt ? opt.value : "",
    });
  };

  const handleOtherStateChange = (opt) => {
    setBillingInfo((prev) => ({
      ...prev,
      billing_other_state: opt ? opt.value : "",
    }));
    updateData({
      ...data,
      billing_other_state: opt ? opt.value : "",
    });
  };

  useEffect(() => {
    if (cfg.skipScrollToTop) return;
    scrollToTop();
  }, [cfg.skipScrollToTop]);

  const fieldDisabled = isDisabled || hasForeignCitizenship;
  const creatableDisabledProps = (() => {
    if (cfg.creatableSelectUsesIsDisabled === true) {
      return { isDisabled };
    }
    if (cfg.creatableSelectUsesIsDisabled === false) {
      return { disabled: isDisabled };
    }
    return {};
  })();

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{ border: "1px solid #e0e0e0" }}
    >
      <Typography variant="h5" className={cfg.headingClassName}>
        Billing Information
      </Typography>

      {cfg.showInvoiceSubtitle && (
        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          Please select the billing address as this is where Liberty will be mailing the invoice.
          It will take 7 to 10 days, and payment online will not be available for 2 to 3 business
          days.
        </Typography>
      )}

      {cfg.supportsBillingOverride && (
        <Dialog
          open={billingOverrideModalOpen}
          onClose={handleOverrideCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Override Preferred Billing</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This professional guardian prefers Principal billing. Are you sure you want to switch
              to Law Firm billing? This will be recorded as a billing override.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleOverrideCancel} color="primary">
              Cancel — Keep Principal
            </Button>
            <Button onClick={handleOverrideConfirm} variant="contained" color="warning">
              Confirm — Switch to Law Firm
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Box sx={{ mb: 4 }}>
        {hasForeignCitizenship && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              p: 2,
              bgcolor: "#fff3cd",
              borderRadius: 1,
              border: "1px solid #ffeaa7",
            }}
          >
            <Info sx={{ color: "#856404", mr: 1 }} />
            <Typography variant="body2" sx={{ color: "#856404" }}>
              Liberty only allows Agency Billing for foreign customers. Law Firm billing has been
              automatically selected.
            </Typography>
          </Box>
        )}

        {hasPGPreferredBilling && !hasForeignCitizenship && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              p: 2,
              bgcolor: "#e3f2fd",
              borderRadius: 1,
              border: "1px solid #90caf9",
            }}
          >
            <Info sx={{ color: "#1565c0", mr: 1 }} />
            <Typography variant="body2" sx={{ color: "#1565c0" }}>
              This professional guardian prefers Principal billing. Principal has been automatically
              selected. Switching to Law Firm will require confirmation.
            </Typography>
          </Box>
        )}

        <Tooltip
          title={
            hasForeignCitizenship
              ? "Liberty only allows Agency Billing for foreign customers"
              : ""
          }
          placement="top"
        >
          <TextField
            select
            label="Who to Bill?"
            name="billing_entity"
            value={billingInfo.billing_entity}
            onChange={handleChange}
            fullWidth
            required
            disabled={fieldDisabled}
          >
            <MenuItem value="Law Firm">Law Firm</MenuItem>
            <MenuItem
              value="Principal"
              disabled={hasForeignCitizenship}
              sx={hasForeignCitizenship ? { opacity: 0.5 } : {}}
            >
              Principal
            </MenuItem>
            <MenuItem
              value="Other"
              disabled={hasForeignCitizenship}
              sx={hasForeignCitizenship ? { opacity: 0.5 } : {}}
            >
              Other
            </MenuItem>
          </TextField>
        </Tooltip>
      </Box>

      {billingInfo.billing_entity === "Law Firm" && (
        <FormControl component="fieldset" sx={{ mb: 4 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Select Billing Location:
          </Typography>
          {loading ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <RadioGroup
              name="billing_lawfirmaddress_name"
              value={billingInfo.billing_lawfirmaddress_name}
              onChange={handleChange}
            >
              {lawFirmLocations.map((location) => (
                <FormControlLabel
                  key={location.value}
                  value={location.value}
                  control={<Radio />}
                  label={`${location.label} - ${location.address}`}
                  disabled={isDisabled}
                />
              ))}
            </RadioGroup>
          )}
        </FormControl>
      )}

      {billingInfo.billing_entity === "Principal" && (
        <>
          <FormControl component="fieldset" sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Select Principal:
            </Typography>
            <RadioGroup
              name="billing_principal_name"
              value={billingInfo.billing_principal_name}
              onChange={handleChange}
            >
              {principalOptions.map((principal) => (
                <FormControlLabel
                  key={principal.value}
                  value={principal.value}
                  control={<Radio />}
                  label={principal.label}
                  disabled={isDisabled}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {cfg.showPrincipalCustNo && (
            <TextField
              disabled={isDisabled}
              label="Customer Number"
              name="custNo"
              value={billingInfo.custNo || ""}
              onChange={handleChange}
              fullWidth
              required
              sx={{ mb: 4 }}
            />
          )}
        </>
      )}

      {billingInfo.billing_entity === "Other" && (
        <>
          <TextField
            label="Name"
            name="billing_other_name"
            value={billingInfo.billing_other_name}
            onChange={handleChange}
            disabled={isDisabled}
            fullWidth
            required
            sx={{ mb: 2 }}
          />

          <TextField
            label="Relationship"
            name="billing_other_relationship"
            value={billingInfo.billing_other_relationship}
            onChange={handleChange}
            disabled={isDisabled}
            fullWidth
            required
            sx={{ mb: 2 }}
          />

          <Typography
            sx={{
              fontSize: "12px",
              color: "rgba(0, 0, 0, 0.6)",
              marginBottom: "4px",
              fontWeight: 500,
            }}
          >
            Search Billing Address
          </Typography>

          <GoogleAutoComplete
            placeholder="Search Billing Address"
            callback={handleOtherAddressAutocomplete}
          />

          <TextField
            label="Street Address"
            name="billing_other_address"
            value={billingInfo.billing_other_address || ""}
            onChange={handleChange}
            disabled={isDisabled}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
          />

          <TextField
            label="ZIP Code"
            name="billing_other_zip"
            value={billingInfo.billing_other_zip || ""}
            onChange={handleChange}
            disabled={isDisabled}
            fullWidth
            sx={{ mb: 2 }}
          />

          {loadingCitiesStates ? (
            <Box display="flex" justifyContent="center" my={2}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <LabeledCreatableSelect
                label="City"
                options={allCities.map((city) => ({ label: city, value: city }))}
                value={
                  billingInfo.billing_other_city
                    ? {
                        label: billingInfo.billing_other_city,
                        value: billingInfo.billing_other_city,
                      }
                    : null
                }
                onChange={handleOtherCityChange}
                placeholder="Select or add a city"
                {...creatableDisabledProps}
              />

              <LabeledCreatableSelect
                label="State"
                options={allStates.map((state) => ({ label: state, value: state }))}
                value={
                  billingInfo.billing_other_state
                    ? {
                        label: billingInfo.billing_other_state,
                        value: billingInfo.billing_other_state,
                      }
                    : null
                }
                onChange={handleOtherStateChange}
                placeholder="Select or add a state"
                {...creatableDisabledProps}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};

BondBillingInformationBase.propTypes = {
  cfg: PropTypes.object.isRequired,
  GoogleAutoComplete: PropTypes.elementType.isRequired,
  LabeledCreatableSelect: PropTypes.elementType.isRequired,
  data: PropTypes.object.isRequired,
  updateData: PropTypes.func.isRequired,
  globalData: PropTypes.object.isRequired,
  isGuest: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

export default BondBillingInformationBase;
